import { Card, ErrorBox, Link, Loading, Page, Stat, Table, Td } from '@elsa/ui';
import { useQuery } from '@tanstack/react-query';
import { fmtDateTime, fmtDay, fmtLei, fmtNum } from '../lib/format.ts';
import { rpc } from '../lib/supabase.ts';
import type { Acquisition, EntityInfo } from '../lib/types.ts';

function Party({
  title,
  e,
  text,
  to,
}: {
  title: string;
  e: EntityInfo | null;
  text: string | null;
  to: string | null;
}) {
  return (
    <Card title={title}>
      <div className="space-y-1 text-sm">
        <div className="font-medium">
          {to && e ? (
            <Link to={to} className="hover:underline">
              {e.entity_name ?? text}
            </Link>
          ) : (
            (e?.entity_name ?? text ?? '—')
          )}
        </div>
        {e ? (
          <div className="text-gri-800">
            {e.numeric_fiscal_number ? <div>CUI {e.numeric_fiscal_number}</div> : null}
            <div>{[e.address, e.city, e.county].filter(Boolean).join(', ')}</div>
            {e.email ? <div>{e.email}</div> : null}
            {e.phone ? <div>{e.phone}</div> : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function AcquisitionPage({ id }: { id: number }) {
  const q = useQuery({
    queryKey: ['acquisition', id],
    queryFn: () => rpc<Acquisition | null>('acquisition', { p_id: id }),
  });
  if (q.isLoading) return <Page title="Achiziția">{<Loading />}</Page>;
  if (q.error) return <Page title="Achiziția">{<ErrorBox error={q.error} />}</Page>;
  const a = q.data;
  if (!a) {
    return (
      <Page title="Achiziția nu se vede">
        <p className="text-sm text-gri-800">
          Achiziția nu există sau e în afara istoricului permis de planul firmei.
        </p>
      </Page>
    );
  }
  const total = a.items.reduce((s, i) => s + Number(i.line_total_ron ?? 0), 0);
  return (
    <Page title={a.name ?? `Achiziția ${a.id}`}>
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat
          label="Data finalizării"
          value={fmtDay(a.finalization_day)}
          sub={fmtDateTime(a.finalization_date)}
        />
        <Stat label="Valoare finală" value={fmtLei(a.closing_value_ron)} />
        <Stat label="Valoare estimată" value={fmtLei(a.estimated_value_ron)} />
        <Stat label="Cod SEAP" value={a.unique_identification_code ?? '—'} sub={`id ${a.id}`} />
      </div>
      <Card title="Detalii">
        <dl className="grid gap-2 text-sm sm:grid-cols-[10rem_1fr]">
          <dt className="text-gri-700">CPV</dt>
          <dd>
            {a.cpv_code ? (
              <Link to={`/app/preturi/${a.cpv_code}`} className="hover:underline">
                {a.cpv_code}
              </Link>
            ) : (
              '—'
            )}{' '}
            {a.cpv_label}
          </dd>
          <dt className="text-gri-700">Publicată</dt>
          <dd>{fmtDateTime(a.publication_date)}</dd>
          {a.description ? (
            <>
              <dt className="text-gri-700">Descriere</dt>
              <dd className="whitespace-pre-line">{a.description}</dd>
            </>
          ) : null}
        </dl>
      </Card>
      <div className="grid gap-3 sm:grid-cols-2">
        <Party
          title="Autoritatea contractantă"
          e={a.authority}
          text={a.contracting_authority_text}
          to={a.contracting_authority_id ? `/app/autoritati/${a.contracting_authority_id}` : null}
        />
        <Party
          title="Furnizorul"
          e={a.supplier}
          text={a.supplier_text}
          to={a.supplier_id ? `/app/furnizori/${a.supplier_id}` : null}
        />
      </div>
      <Card
        title={`Produse (${a.items.length})`}
        actions={<span className="text-sm tabular-nums">{fmtLei(total)}</span>}
      >
        <Table
          head={['Produsul', 'CPV', 'Cantitate', 'Preț unitar', 'Total']}
          empty={!a.items.length}
        >
          {a.items.map((i) => (
            <tr key={i.id}>
              <Td className="max-w-md">
                <div className="font-medium">{i.name}</div>
                {i.description ? <div className="text-xs text-gri-700">{i.description}</div> : null}
              </Td>
              <Td className="whitespace-nowrap">
                {i.cpv_code ? (
                  <Link to={`/app/preturi/${i.cpv_code}`} className="hover:underline">
                    {i.cpv_code}
                  </Link>
                ) : (
                  '—'
                )}
              </Td>
              <Td className="whitespace-nowrap tabular-nums">
                {fmtNum(i.quantity)} {i.measure_unit}
              </Td>
              <Td className="whitespace-nowrap text-right tabular-nums">
                {fmtLei(i.unit_price_ron)}
              </Td>
              <Td className="whitespace-nowrap text-right tabular-nums">
                {fmtLei(i.line_total_ron)}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
    </Page>
  );
}
